import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isEmergencyAdmission, isOpenStayStatus } from '../../utils/admission';
import { joinFullName } from '../../utils/patientName';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  AlertTriangle,
  User,
  BedDouble,
  Stethoscope,
  Activity,
  Calendar,
  Users,
  MapPin,
  ExternalLink,
  Hash,
  Banknote,
  ClipboardList,
  ChevronRight,
  ReceiptText,
  Receipt,
  Plus,
  Search,
  CheckCircle2,
  Clock,
  X,
  ShoppingCart,
  Package,
  Printer,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Handshake,
  ArrowRightLeft,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatOrdinalDateTimeLong, formatOrdinalDateTimeInTimeZone, formatDateLabel } from '../../utils/dateFormat';
import { DEFAULT_HOSPITAL_TIMEZONE } from '../../lib/timezones';
import {
  useAdmission,
  useServices,
  useActiveServiceTypes,
  useAdmissionCharges,
  useAddAdmissionServices,
  useRefreshAdmissionBedCharges,
  useSettings,
  useDischargeAdmission,
  useAdmissionBillingStatus,
  useUpdateAdmissionChargeQuantity,
  useDeleteAdmissionCharge,
  useBulkDeleteAdmissionCharges,
  useDeleteInvoice,
  useBulkDeleteInvoices,
  useUpdateAdmission,
} from '../../services/httpServices/queries';
import type {
  AdmissionBillingInvoice,
  AdmissionCharge,
} from '../../services/httpServices/ipdService';
import { AdmissionInvoiceModal } from '../../components/ui/admission-invoice-modal';
import { AdmissionReferralModal } from '../../components/ui/admission-referral-modal';
import { AdmissionTransferBedModal } from '../../components/ui/admission-transfer-bed-modal';
import { AdmissionCareTeamModal } from '../../components/ui/admission-care-team-modal';
import { AdmissionDetailsModal } from '../../components/ui/admission-details-modal';
import { ReferralRemoveDialog } from '../../components/ui/referral-remove-dialog';
import { PatientDetailsModal } from '../../components/ui/patient-details-modal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { ipdService } from '../../services/httpServices/ipdService';
import { QuantityStepper } from '../../components/ui/quantity-stepper';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { InfoHint } from '../../components/ui/InfoHint';
import { AdmissionPaymentBadge } from '../../components/ui/admission-payment-badge';
import {
  isRiskFundInvoiceStatus,
  paymentStateFromBilling,
  resolveAdmissionPaymentState,
} from '../../utils/admissionPayment';
import { usePermissions } from '../../hooks/usePermissions';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import {
  buildAdmissionConfirmationHtml,
  buildDischargeConfirmationHtml,
} from '../../utils/admissionPrint';
import { openPrintWindow } from '../../utils/printWindow';
import { isDocumentSignatureEnabled } from '../../utils/signatureSettings';
import {
  resolveServicePricing,
  isEmergencyService,
  isEmergencyServiceType,
  isQuantityBilled,
  defaultUnitLabel,
  formatQuantity,
  normalizeServiceTypeCode,
  round2,
  serviceQuantityBounds,
} from '../../utils/servicePricing';

const statusStyles: Record<string, string> = {
  ADMITTED: 'bg-primary/10 text-primary',
  DISCHARGE_INITIATED: 'bg-warning/10 text-warning',
  DISCHARGED: 'bg-success/10 text-success',
  TRANSFERRED: 'bg-secondary/10 text-secondary',
  CANCELLED: 'bg-danger/10 text-danger',
};

const money = (value: number) =>
  `৳${(Number.isFinite(value) ? value : 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

/** Stepper bounds for a charge line, taken from its service's billing rules. */
function chargeQuantityBounds(charge: AdmissionCharge) {
  return serviceQuantityBounds(charge.service || {});
}

export function AdmissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || '/ipd';

  const { data, isLoading, error } = useAdmission(id);

  const { hasAccess, hasPermission, hasAnyPermission } = usePermissions();
  const { data: chargesData, isLoading: chargesLoading } = useAdmissionCharges(id);
  const { data: settingsData } = useSettings();
  const refreshBedCharges = useRefreshAdmissionBedCharges();
  const dischargeAdmission = useDischargeAdmission();
  // Removing a referral reuses the admission update endpoint: `referralId: null`
  // clears the partner, the amount, and any unpaid payout for this stay.
  const updateAdmission = useUpdateAdmission();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // Patient profile dialog opened from the Admission Details header.
  const [showPatientModal, setShowPatientModal] = useState(false);
  // Bed transfer and post-admission care-team management.
  const [showTransferBed, setShowTransferBed] = useState(false);
  const [showCareTeam, setShowCareTeam] = useState(false);
  // Primary doctor / admission type / referrer dialog, opened from the Doctor,
  // Type and Referrer tiles. Those fields are edited inline — there is no full
  // admission edit page.
  const [showAdmissionDetails, setShowAdmissionDetails] = useState(false);
  // Focused referral-commission dialog; the admission's other fields are edited
  // inline, so there is no full admission edit page.
  const [showReferralModal, setShowReferralModal] = useState(false);
  // Destructive confirmation before clearing the referral and its payout.
  const [confirmRemoveReferral, setConfirmRemoveReferral] = useState(false);
  // Amount shown in the confirm dialog when a discharge leaves a balance.
  const [dueConfirm, setDueConfirm] = useState<{ amount: number } | null>(null);

  // ── Removing billed-in-error entries ──
  // Unbilled charges and unpaid invoices are mistakes that must be removable
  // from this screen. Anything already paid (or written off) stays put: the
  // backend refuses it, and the UI never offers it.
  const updateChargeQuantity = useUpdateAdmissionChargeQuantity();
  const deleteCharge = useDeleteAdmissionCharge();
  const bulkDeleteCharges = useBulkDeleteAdmissionCharges();
  const deleteInvoice = useDeleteInvoice();
  const bulkDeleteInvoices = useBulkDeleteInvoices();
  const { data: billingStatusData, isLoading: billingStatusLoading } =
    useAdmissionBillingStatus(id);
  const [selectedChargeIds, setSelectedChargeIds] = useState<string[]>([]);
  const [chargeToDelete, setChargeToDelete] = useState<AdmissionCharge | null>(null);
  const [confirmBulkCharges, setConfirmBulkCharges] = useState(false);
  // Quantity typed by the user before the API answers; keeps the stepper snappy
  // while the re-priced row comes back from the server.
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, number>>({});
  const [savingChargeId, setSavingChargeId] = useState<string | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [invoiceToDelete, setInvoiceToDelete] =
    useState<AdmissionBillingInvoice | null>(null);
  const [confirmBulkInvoices, setConfirmBulkInvoices] = useState(false);

  const admission = data?.data;
  const patient = admission?.patient || {};
  const bed = admission?.bed;
  const patientId = admission?.patientId || patient?.id;

  const fullName =
    joinFullName(patient) ||
    patient.registrationNumber ||
    'Unknown patient';
  const status = admission?.status || 'ADMITTED';
  const admittedAt = admission?.admittedAt || admission?.admissionDate;
  const dischargedAt = admission?.dischargedAt;

  // `dischargeDueAmount` is a snapshot taken when the patient left; the visit's
  // live invoice balance is what actually remains. They diverge once a carried
  // balance is collected later from Billing → Invoices.
  const dischargeDueSnapshot = Number(admission?.dischargeDueAmount || 0);
  const liveDueAmount = Number(admission?.dueAmount);
  const dischargeOutstanding =
    dischargeDueSnapshot > 0
      ? Number.isFinite(liveDueAmount)
        ? Math.max(0, liveDueAmount)
        : dischargeDueSnapshot
      : 0;

  const doctorName = admission?.doctor?.name || 'Unknown';
  const nurseName = admission?.nurse?.name || 'N/A';
  const referredByName = admission?.referredBy?.name || 'N/A';
  const isEmergency = isEmergencyAdmission(admission);
  const wardName = bed?.wardEntity?.wardName || bed?.ward || (isEmergency ? 'Emergency' : '—');
  const bedLabel = bed?.bedNumber || (isEmergency ? 'No bed assigned' : 'Not assigned');
  // Bed types are stored as codes (GENERAL, ICU, …) — show them in a readable
  // form while keeping short clinical acronyms uppercase.
  const bedType = bed?.type
    ? String(bed.type)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b(icu|ccu|nicu|hdu|vip)\b/g, (code) => code.toUpperCase())
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : 'N/A';
  const bedPrice = Number(bed?.price || 0);

  const charges = useMemo(() => chargesData?.data?.charges ?? [], [chargesData]);
  const totals = chargesData?.data?.totals || { unbilled: 0, billed: 0, total: 0 };
  const visitId = chargesData?.data?.visitId || admission?.visitId;

  // Invoice-based figures. `admission.totalAmount` / `paidAmount` / `dueAmount`
  // come from the visit's non-cancelled invoices (see attachBillingSummaries),
  // so these are the exact numbers the IPD list shows in its Total/Due columns.
  // The charge totals above are pre-invoice (and pre-tax), which is why they
  // must not be used as the "Total" — that made the two screens disagree.
  const invoicedTotal = Number(admission?.totalAmount || 0);
  const paidTotal = Number(admission?.paidAmount || 0);
  const dueTotal = Number(admission?.dueAmount || 0);
  const canBill = hasAccess('/billing/invoices') || hasPermission('billing:read');
  const canAddServices = status !== 'CANCELLED';
  const showServiceCart = status !== 'DISCHARGED';
  const canRefreshBedCharges =
    hasPermission('ipd:update') && status !== 'CANCELLED';
  // `TRANSFERRED` is still an open stay (a bed move re-labels the admission but
  // the patient is in the ward), so every open-stay action must accept it too —
  // otherwise a bed transfer would hide Discharge and Transfer bed.
  const isOpenStay = isOpenStayStatus(status);
  const canDischargeAdmission = hasPermission('ipd:update') && isOpenStay;
  // The referral commission can be set or cleared on any live admission.
  const canManageReferral =
    hasPermission('ipd:update') && status !== 'CANCELLED';
  // Beds can be changed while the stay is open; a discharged/cancelled stay is
  // historical and must keep the bed it actually used.
  const canManageBed = hasPermission('ipd:update') && isOpenStay;
  const canManageCareTeam =
    hasPermission('ipd:update') && status !== 'CANCELLED';
  // Admission type and referring doctor are administrative facts of a live stay.
  const canManageAdmissionMeta =
    hasPermission('ipd:update') && status !== 'CANCELLED';
  // Changing the admission date is a separate, sensitive capability — the same
  // one the new-admission form uses to show its Admission Date & Time field.
  const canBackdateAdmission = hasPermission('ipd:admission:backdate');
  // Hospital timezone (Settings → General) for showing and editing the
  // admission timestamp — mirrors the new-admission form.
  const hospitalTimezone = useMemo(() => {
    const value = settingsData?.data?.find((s: any) => s.key === 'hospital_timezone')?.value;
    return (typeof value === 'string' ? value.trim() : '') || DEFAULT_HOSPITAL_TIMEZONE;
  }, [settingsData]);
  // ── Delete permissions and candidates ──
  // Charges are removed by IPD or billing staff; invoices need the billing
  // delete right. Paid / written-off records are never offered for deletion.
  const canDeleteCharges = hasPermission('ipd:charge:delete');
  // Quantity editing was added on top of the existing IPD update right, so a
  // role that could already adjust an admission keeps the stepper without an
  // extra permission grant.
  const canEditChargeQuantity = hasAnyPermission([
    'ipd:charge:update',
    'ipd:update',
  ]);
  const canDeleteInvoices = hasAnyPermission([
    'billing:invoice:delete_unpaid',
    'billing:invoice:delete',
    'billing:delete',
  ]);
  const selectableCharges = useMemo(
    () => charges.filter((charge) => !charge.isInvoiced),
    [charges],
  );
  const selectedCharges = useMemo(
    () => selectableCharges.filter((charge) => selectedChargeIds.includes(charge.id)),
    [selectableCharges, selectedChargeIds],
  );
  const visitInvoices = useMemo(
    () => billingStatusData?.data?.invoices ?? [],
    [billingStatusData],
  );
  const deletableInvoices = useMemo(
    () =>
      visitInvoices.filter(
        (invoice) =>
          Number(invoice.paidAmount || 0) <= 0 &&
          !isRiskFundInvoiceStatus(invoice.status),
      ),
    [visitInvoices],
  );
  const selectedInvoices = useMemo(
    () => deletableInvoices.filter((invoice) => selectedInvoiceIds.includes(invoice.id)),
    [deletableInvoices, selectedInvoiceIds],
  );
  const allChargesSelected =
    selectableCharges.length > 0 && selectedCharges.length === selectableCharges.length;
  const allInvoicesSelected =
    deletableInvoices.length > 0 && selectedInvoices.length === deletableInvoices.length;

  // Payment state for the header badge. A Risk Fund write-off clears the
  // invoice's due, so deriving the state from `dueTotal > 0` would mislabel a
  // written-off bill as paid — read the live invoice statuses instead.
  const paymentState =
    paymentStateFromBilling({
      invoices: visitInvoices,
      outstandingDue: billingStatusData?.data?.outstandingDue,
      unbilledCount: billingStatusData?.data?.unbilledCount,
      // A cancelled stay is void: its unbilled charges can never be invoiced,
      // so they must not mask the Risk Fund write-off made on cancellation.
      ignoreUnbilled: status === 'CANCELLED',
    }) ?? resolveAdmissionPaymentState(admission);
  const liveDueTotal =
    Number(billingStatusData?.data?.outstandingDue ?? dueTotal) || 0;
  const writtenOffTotal =
    paymentState === 'RISK_FUND'
      ? Math.max(0, Number((invoicedTotal - paidTotal).toFixed(2)))
      : 0;

  const toggleChargeSelection = (chargeId: string) =>
    setSelectedChargeIds((prev) =>
      prev.includes(chargeId)
        ? prev.filter((value) => value !== chargeId)
        : [...prev, chargeId],
    );

  const toggleInvoiceSelection = (invoiceId: string) =>
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((value) => value !== invoiceId)
        : [...prev, invoiceId],
    );

  /**
   * Re-prices one unbilled charge for a new quantity. The row shows the typed
   * value immediately and falls back to the server value once it answers (the
   * backend may resolve a minimum number of units).
   */
  const handleChargeQuantityChange = async (
    charge: AdmissionCharge,
    nextQuantity: number,
  ) => {
    if (!id) return;
    const previousDraft = quantityDrafts[charge.id];
    setQuantityDrafts((prev) => ({ ...prev, [charge.id]: nextQuantity }));
    setSavingChargeId(charge.id);
    try {
      await updateChargeQuantity.mutateAsync({
        admissionId: id,
        chargeId: charge.id,
        quantity: nextQuantity,
      });
      setQuantityDrafts((prev) => {
        const next = { ...prev };
        delete next[charge.id];
        return next;
      });
    } catch (err: any) {
      // Roll the stepper back to what it showed before this attempt.
      setQuantityDrafts((prev) => {
        const next = { ...prev };
        if (previousDraft === undefined) delete next[charge.id];
        else next[charge.id] = previousDraft;
        return next;
      });
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not change the quantity.',
      );
    } finally {
      setSavingChargeId(null);
    }
  };

  /** Unlinks the referral partner and clears its flat commission for this stay. */
  const handleRemoveReferral = async () => {
    if (!admission) return;
    try {
      await updateAdmission.mutateAsync({
        id: admission.id,
        data: { referralId: null, referralFlatAmount: 0 },
      });
      toast.success('Referral removed from this admission');
      setConfirmRemoveReferral(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not remove the referral.',
      );
    }
  };

  /** Removes one unbilled charge (patient-ledger debit is reversed server-side). */
  const handleDeleteCharge = async () => {
    if (!id || !chargeToDelete) return;
    try {
      await deleteCharge.mutateAsync({ admissionId: id, chargeId: chargeToDelete.id });
      toast.success('Charge removed from the bill');
      setSelectedChargeIds((prev) =>
        prev.filter((value) => value !== chargeToDelete.id),
      );
      setChargeToDelete(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not remove the charge.',
      );
    }
  };

  /** Removes every selected unbilled charge in one transactional request. */
  const handleBulkDeleteCharges = async () => {
    if (!id || selectedCharges.length === 0) return;
    try {
      const res = await bulkDeleteCharges.mutateAsync({
        admissionId: id,
        ids: selectedCharges.map((charge) => charge.id),
      });
      const removed = res?.data?.deleted?.length || 0;
      const blocked = res?.data?.blocked?.length || 0;

      if (removed > 0) {
        toast.success(
          `${removed} charge${removed === 1 ? '' : 's'} removed from the bill`,
        );
      }
      if (blocked > 0) {
        toast.warning(
          `${blocked} charge${blocked === 1 ? ' is' : 's are'} already on an invoice and ${
            blocked === 1 ? 'was' : 'were'
          } kept — delete that invoice first.`,
        );
      }
      setSelectedChargeIds([]);
      setConfirmBulkCharges(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not remove the selected charges.',
      );
    }
  };

  /**
   * Deletes one unpaid invoice. Its charges are released back to unbilled and
   * the visit's Total / Paid / Due recalculate from what is left.
   */
  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      await deleteInvoice.mutateAsync(invoiceToDelete.id);
      toast.success(
        `Invoice ${invoiceToDelete.invoiceNumber || ''} deleted — its charges are unbilled again`,
      );
      setSelectedInvoiceIds((prev) =>
        prev.filter((value) => value !== invoiceToDelete.id),
      );
      setInvoiceToDelete(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not delete the invoice.',
      );
    }
  };

  /** Deletes every selected unpaid invoice in one request. */
  const handleBulkDeleteInvoices = async () => {
    if (selectedInvoices.length === 0) return;
    try {
      const res = await bulkDeleteInvoices.mutateAsync(
        selectedInvoices.map((invoice) => invoice.id),
      );
      const removed = res?.data?.deleted?.length || 0;
      const blocked = res?.data?.blocked?.length || 0;

      if (removed > 0) {
        toast.success(
          `${removed} invoice${removed === 1 ? '' : 's'} deleted — ${
            removed === 1 ? 'its charges are' : 'their charges are'
          } unbilled again`,
        );
      }
      if (blocked > 0) {
        toast.warning(
          `${blocked} invoice${blocked === 1 ? ' has' : 's have'} a payment recorded and ${
            blocked === 1 ? 'was' : 'were'
          } kept.`,
        );
      }
      setSelectedInvoiceIds([]);
      setConfirmBulkInvoices(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not delete the selected invoices.',
      );
    }
  };

  /**
   * Re-checks the nightly bed charge for this admission. Posts any outstanding
   * nights and reports a plain-language outcome, so staff can recover a charge
   * the scheduled job has not produced yet.
   */
  const handleRefreshBedCharges = async () => {
    if (!id) return;
    try {
      const res = await refreshBedCharges.mutateAsync(id);
      const data = res?.data;
      const posted = Number(data?.postedNights || 0);
      const total = Number(data?.bedChargeCount || 0);
      switch (data?.reason) {
        case 'POSTED':
          toast.success(
            `Added ${posted} bed night${posted === 1 ? '' : 's'} · ${total} billed in total.`,
          );
          break;
        case 'NO_BED_PRICE':
          toast.warning(
            'This bed has no nightly rate set, so no bed charge can be added. Set a price on the bed first.',
          );
          break;
        case 'NO_BED':
          toast.warning(
            'No bed is assigned to this admission yet, so there is nothing to bill nightly.',
          );
          break;
        case 'CLOSED':
          toast.info('This admission is closed — no further bed nights are due.');
          break;
        case 'UP_TO_DATE':
        default:
          toast.info(
            'No new bed night to add yet. The next one posts after the daily cutoff.',
          );
          break;
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Could not refresh the bed charge. Please try again.',
      );
    }
  };

  /**
   * Discharges the patient. `allowDue` is only set from a surface that has
   * already shown the unpaid balance (the invoice dialog's due button or the
   * confirmation dialog below), so the due is always explicit.
   *
   * A failure is surfaced verbatim — the backend names the exact reason (an
   * unbilled charge or an unconfirmed balance).
   */
  const runDischarge = async (allowDue: boolean) => {
    if (!id) return;
    try {
      await dischargeAdmission.mutateAsync({
        id,
        data: allowDue ? { allowDue: true } : {},
      });
      toast.success(
        allowDue
          ? `${fullName} discharged — balance carried as due`
          : `${fullName} discharged`,
      );
      setShowInvoiceModal(false);
      setDueConfirm(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to discharge the patient.',
      );
    }
  };

  /** From the invoice dialog, whose button is labelled with the amount due. */
  const handleDischarge = () => void runDischarge(true);

  /**
   * Header action: checks the bill first so the outcome is predictable —
   * unbilled charges open the invoice dialog, an unpaid balance asks for
   * confirmation with the exact amount, and a settled bill discharges directly.
   */
  const handleDischargeClick = async () => {
    if (!id) return;
    try {
      const res = await ipdService.getAdmissionBillingStatus(id);
      const billing = res.data;
      if (billing && !billing.canDischarge) {
        toast.error(
          `Create the final invoice first — ${billing.unbilledCount} charge(s) worth ${money(
            billing.unbilledAmount,
          )} are not invoiced.`,
        );
        setShowInvoiceModal(true);
        return;
      }
      if (billing?.hasOutstandingDue) {
        setDueConfirm({ amount: billing.outstandingDue });
        return;
      }
      await runDischarge(false);
    } catch {
      // Let the discharge endpoint decide; it reports the exact reason.
      await runDischarge(false);
    }
  };

  const hospital = useMemo(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      nameBn: find('hospital_name_bn'),
      logo: find('hospital_logo'),
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      certified: find('hospital_certified'),
      signature: find('hospital_signature'),
      seal: find('hospital_seal'),
      signatoryName: find('hospital_signatory_name'),
      showSignature: isDocumentSignatureEnabled(find, 'admission'),
    };
  }, [settingsData]);

  const handlePrintConfirmation = () => {
    if (!admission) return;
    const html =
      status === 'DISCHARGED'
        ? buildDischargeConfirmationHtml(admission, hospital)
        : buildAdmissionConfirmationHtml(admission, hospital);
    const label = status === 'DISCHARGED' ? 'discharge paper' : 'admission confirmation';
    if (!openPrintWindow(html)) {
      toast.error(`Please allow pop-ups to print the ${label}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !admission) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text transition-colors"
        >
          <ArrowLeft size={16} />
          Back to IPD
        </button>
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-xl text-danger">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">Failed to load admission details.</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`space-y-6 ${
        showServiceCart
          ? 'xl:flex xl:h-full xl:flex-col xl:gap-6 xl:space-y-0'
          : ''
      }`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 min-w-0">
        <div className="flex items-start gap-3 min-w-0">
          <button
            onClick={() => navigate(backTo)}
            className="mt-0.5 p-2 rounded-lg border border-border text-text-muted hover:text-text hover:bg-surface transition-colors shrink-0"
            title="Back to IPD admissions"
            aria-label="Back to IPD admissions"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
            <User size={22} className="text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-text tracking-tight truncate">{fullName}</h1>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                  statusStyles[status] || 'bg-text-muted/10 text-text-muted'
                }`}
              >
                {status.replace(/_/g, ' ')}
              </span>
              <AdmissionPaymentBadge
                status={paymentState}
                size="md"
                detail={
                  paymentState === 'RISK_FUND' && writtenOffTotal > 0
                    ? `${money(writtenOffTotal)} written off`
                    : paymentState === 'DUE' && liveDueTotal > 0
                      ? money(liveDueTotal)
                      : undefined
                }
              />
              {isEmergency && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[0.6875rem] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-amber-300/70"
                  title="Emergency admission — registered without a bed (a bed can be assigned later)"
                >
                  <AlertTriangle size={11} />
                  Emergency
                </span>
              )}
            </div>
            <p className="text-xs font-mono text-text-muted mt-0.5">
              {patient.registrationNumber || '—'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {canManageReferral && (
            <button
              onClick={() => setShowReferralModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-text rounded-xl hover:bg-background transition-colors text-sm font-medium"
              title="Add or change the referral commission for this admission"
            >
              <Handshake size={16} />
              {admission.referralId ? 'Edit Referral' : 'Add Referral'}
            </button>
          )}
          {canDischargeAdmission && (
            <button
              onClick={handleDischargeClick}
              disabled={dischargeAdmission.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold disabled:opacity-60"
              title="Discharge the patient — an unpaid balance can be carried and collected later"
            >
              {dischargeAdmission.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle2 size={16} />
              )}
              Discharge
            </button>
          )}
          <button
            onClick={handlePrintConfirmation}
            className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-text rounded-xl hover:bg-background transition-colors text-sm font-medium"
            title={
              status === 'DISCHARGED'
                ? 'Print the discharge confirmation paper'
                : status === 'CANCELLED'
                  ? 'Print the cancelled admission record'
                  : 'Print the admission confirmation paper'
            }
          >
            <Printer size={16} />
            {status === 'DISCHARGED'
              ? 'Print Discharge Paper'
              : status === 'CANCELLED'
                ? 'Print Cancelled Record'
                : 'Print Confirmation'}
          </button>
          {patientId && (
            <Link
              to={`/patients/${patientId}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-text rounded-xl hover:bg-background transition-colors text-sm font-medium"
            >
              <ExternalLink size={16} />
              View Full Profile
            </Link>
          )}
        </div>
      </div>

      <div
        className={`grid grid-cols-1 gap-6 ${
          showServiceCart
            ? 'xl:min-h-0 xl:flex-1 xl:grid-cols-[minmax(0,1fr)_460px] xl:grid-rows-[minmax(0,1fr)]'
            : ''
        }`}
      >
      {/* Service cart (right rail on desktop, before the details on mobile) */}
      {showServiceCart && (
        <div className="min-w-0 xl:col-start-2 xl:row-start-1">
          {canAddServices ? (
            <ServiceCartPanel
              admissionId={admission.id}
              // Price overwrites mirror the API rule exactly: the admission type
              // itself must be Emergency (a bed-less stay is not enough).
              isEmergency={
                String(admission.admissionType ?? '').toUpperCase() === 'EMERGENCY'
              }
            />
          ) : (
            <div className="bg-surface border border-border rounded-2xl p-6 text-center shadow-sm">
              <div className="w-12 h-12 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-3">
                <AlertCircle size={22} className="text-danger" />
              </div>
              <p className="text-sm font-semibold text-text">Cannot add services</p>
              <p className="text-xs text-text-muted mt-1">
                This admission is cancelled, so new charges are disabled.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Details */}
      <div
        className={`space-y-6 min-w-0 ${
          showServiceCart
            ? 'xl:col-start-1 xl:row-start-1 xl:min-h-0 xl:overflow-y-auto xl:pr-1'
            : ''
        }`}
      >
          {/* Admission details */}
          <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 bg-gradient-to-r from-primary/[0.06] via-transparent to-transparent px-4 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <ClipboardList size={16} />
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold uppercase tracking-wide text-text">
                    Admission Details
                  </h2>
                  <p className="truncate text-[0.6875rem] text-text-muted">
                    Stay, care team and bed
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {canManageBed && (
                  <button
                    type="button"
                    onClick={() => setShowTransferBed(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-1.5 text-xs font-semibold text-text transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                    title="Move the patient to another available bed"
                  >
                    <ArrowRightLeft size={14} className="text-primary" />
                    Transfer bed
                  </button>
                )}
                {canManageCareTeam && (
                  <button
                    type="button"
                    onClick={() => setShowCareTeam(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-1.5 text-xs font-semibold text-text transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                    title="View or update the doctors, nurses and other staff on this admission"
                  >
                    <Users size={14} className="text-primary" />
                    Care team
                  </button>
                )}
                {canManageAdmissionMeta && (
                  <button
                    type="button"
                    onClick={() => setShowAdmissionDetails(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-1.5 text-xs font-semibold text-text transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                    title="Change the admission date, primary doctor, admission type or referring doctor"
                  >
                    <Pencil size={14} className="text-primary" />
                    Edit details
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowPatientModal(true)}
                  className="group inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-1.5 text-xs font-semibold text-text transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                  title="Open the patient's profile details"
                >
                  <User size={14} className="text-primary" />
                  Patient Details
                  <ChevronRight
                    size={13}
                    className="text-text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                  />
                </button>
              </div>
            </div>

            <div className="space-y-2.5 p-3.5">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                <InfoTile
                  icon={<BedDouble size={16} className="text-primary" />}
                  label="Bed"
                  value={bedLabel}
                />
                <InfoTile
                  icon={<MapPin size={16} className="text-primary" />}
                  label="Ward"
                  value={wardName}
                />
                <InfoTile
                  icon={<Banknote size={16} className="text-primary" />}
                  label="Rate"
                  value={
                    bedPrice > 0 ? (
                      <p className="text-[0.8125rem] font-semibold leading-snug text-text">
                        {money(bedPrice)}
                        <span className="ml-1 text-[0.6875rem] font-normal text-text-muted">
                          / night
                        </span>
                      </p>
                    ) : (
                      <p
                        className="text-[0.8125rem] font-semibold leading-snug text-text-muted"
                        title="Set a price on this bed to bill it nightly"
                      >
                        Not set
                      </p>
                    )
                  }
                />
                <InfoTile
                  icon={<Stethoscope size={16} className="text-primary" />}
                  label="Doctor"
                  value={
                    admission.doctorId || doctorName !== 'Unknown' ? (
                      doctorName
                    ) : (
                      <span className="text-[0.8125rem] font-normal text-text-muted">
                        Not set
                      </span>
                    )
                  }
                  action={
                    canManageAdmissionMeta ? (
                      <EditTileButton
                        label={
                          admission.doctorId
                            ? 'Change the primary doctor'
                            : 'Assign a primary doctor'
                        }
                        onClick={() => setShowAdmissionDetails(true)}
                      />
                    ) : undefined
                  }
                />
                <InfoTile
                  icon={<Users size={16} className="text-primary" />}
                  label="Nurse"
                  value={nurseName}
                />
                <InfoTile
                  icon={<Stethoscope size={16} className="text-primary" />}
                  label="Referrer"
                  value={
                    admission.referredById || referredByName !== 'N/A' ? (
                      referredByName
                    ) : (
                      <span className="text-[0.8125rem] font-normal text-text-muted">
                        Not set
                      </span>
                    )
                  }
                  action={
                    canManageAdmissionMeta ? (
                      <EditTileButton
                        label={
                          admission.referredById
                            ? 'Change the referring doctor'
                            : 'Add a referring doctor'
                        }
                        onClick={() => setShowAdmissionDetails(true)}
                      />
                    ) : undefined
                  }
                />
                <InfoTile
                  icon={<Activity size={16} className="text-primary" />}
                  label="Type"
                  value={(admission.admissionType || 'N/A').replace(/_/g, ' ')}
                  capitalize
                  action={
                    canManageAdmissionMeta ? (
                      <EditTileButton
                        label={
                          isEmergency
                            ? 'Emergency admissions keep their type — the doctor and referrer can still be changed'
                            : 'Change the admission type'
                        }
                        onClick={() => setShowAdmissionDetails(true)}
                      />
                    ) : undefined
                  }
                />
                <InfoTile
                  icon={<Hash size={16} className="text-primary" />}
                  label="Status"
                  value={
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide ${
                        statusStyles[status] || 'bg-text-muted/10 text-text-muted'
                      }`}
                    >
                      {status.replace(/_/g, ' ')}
                    </span>
                  }
                />
                <InfoTile
                  icon={<BedDouble size={16} className="text-primary" />}
                  label="Bed Type"
                  value={bedType}
                />
                <InfoTile
                  icon={<Calendar size={16} className="text-primary" />}
                  label="Admitted"
                  value={formatOrdinalDateTimeInTimeZone(admittedAt, hospitalTimezone)}
                  className="col-span-2"
                  action={
                    canManageAdmissionMeta && canBackdateAdmission ? (
                      <EditTileButton
                        label="Change the admission date & time (backdate)"
                        onClick={() => setShowAdmissionDetails(true)}
                      />
                    ) : undefined
                  }
                />
                {dischargedAt && (
                  <InfoTile
                    icon={<Calendar size={16} className="text-primary" />}
                    label="Discharged"
                    value={formatOrdinalDateTimeLong(dischargedAt)}
                    className="col-span-2"
                  />
                )}
              </div>

              {(admission.admissionReason ||
                admission.referralDetails ||
                admission.referralId) && (
                <div
                  className={`grid gap-2 ${
                    [admission.admissionReason, admission.referralDetails, admission.referralId]
                      .filter(Boolean)
                      .length >= 3
                      ? 'sm:grid-cols-3'
                      : [admission.admissionReason, admission.referralDetails, admission.referralId]
                            .filter(Boolean)
                            .length === 2
                        ? 'sm:grid-cols-2'
                        : ''
                  }`}
                >
                  {admission.admissionReason && (
                    <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                      <p className="mb-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                        Reason / Diagnosis
                      </p>
                      <p className="whitespace-pre-wrap text-[0.8125rem] leading-snug text-text">
                        {admission.admissionReason}
                      </p>
                    </div>
                  )}
                  {admission.referralDetails && (
                    <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                      <p className="mb-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                        Referral Details
                      </p>
                      <p className="whitespace-pre-wrap text-[0.8125rem] leading-snug text-text">
                        {admission.referralDetails}
                      </p>
                    </div>
                  )}
                  {/* Flat IPD referral commission booked to Finance → Payouts. */}
                  {admission.referralId && (
                    <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="mb-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">
                            Referral Commission
                          </p>
                          <p className="text-[0.8125rem] font-semibold leading-snug text-primary">
                            {money(Number(admission.referralFlatAmount) || 0)}
                          </p>
                          <p className="truncate text-[0.6875rem] leading-snug text-text-muted">
                            {admission.referral?.name || 'Referral partner'}
                            {Number(admission.referralFlatAmount) > 0
                              ? ' · flat payout'
                              : ' · no payout (৳0)'}
                          </p>
                        </div>
                        {canManageReferral && (
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveReferral(true)}
                            title="Remove the referral partner and its commission"
                            aria-label="Remove referral"
                            className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {dischargeDueSnapshot > 0 &&
                (dischargeOutstanding > 0 ? (
                  <div className="rounded-xl border border-warning/20 bg-warning/5 px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Banknote size={13} className="text-warning" />
                      <p className="text-[0.625rem] font-bold uppercase tracking-wider text-warning">
                        Balance carried at discharge
                      </p>
                    </div>
                    <p className="mt-1 text-[0.8125rem] leading-snug text-text">
                      {money(dischargeOutstanding)} is still unpaid
                      {dischargeOutstanding < dischargeDueSnapshot
                        ? ` of the ${money(dischargeDueSnapshot)} carried when the patient left`
                        : ' on this visit'}
                      .
                    </p>
                    <p className="mt-0.5 text-[0.6875rem] text-text-muted">
                      {admission.dischargeDueDate
                        ? `Promised by ${formatDateLabel(admission.dischargeDueDate)}. `
                        : ''}
                      Check Billing → Invoices for the live balance.
                    </p>
                    {admission.dischargeDueNote && (
                      <p className="mt-1 text-[0.6875rem] italic text-text-muted">
                        “{admission.dischargeDueNote}”
                      </p>
                    )}
                  </div>
                ) : (admission.invoiceCount ?? 0) === 0 ? (
                  <div className="rounded-xl border border-warning/20 bg-warning/5 px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <AlertCircle size={13} className="text-warning" />
                      <p className="text-[0.625rem] font-bold uppercase tracking-wider text-warning">
                        Carried balance no longer invoiced
                      </p>
                    </div>
                    <p className="mt-1 text-[0.8125rem] leading-snug text-text">
                      {money(dischargeDueSnapshot)} was carried when the patient left, but the
                      invoice was deleted — raise a new invoice if it still has to be
                      collected.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-success/20 bg-success/5 px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 size={13} className="text-success" />
                      <p className="text-[0.625rem] font-bold uppercase tracking-wider text-success">
                        Balance at discharge settled
                      </p>
                    </div>
                    <p className="mt-1 text-[0.8125rem] leading-snug text-text">
                      {money(dischargeDueSnapshot)} was carried when the patient left and has
                      since been paid in full.
                    </p>
                  </div>
                ))}

              {admission.dischargeSummary && (
                <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
                  <div className="mb-1 flex items-center gap-1.5">
                    <ReceiptText size={13} className="text-text-muted" />
                    <p className="text-[0.625rem] font-bold uppercase tracking-wider text-text-muted">
                      Discharge Summary
                    </p>
                  </div>
                  <p className="whitespace-pre-wrap text-[0.8125rem] leading-snug text-text-muted">
                    {admission.dischargeSummary}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Services & Charges */}
          <div className="bg-surface border border-border rounded-2xl p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                  Services &amp; Charges
                </h2>
                {chargesLoading && <Loader2 size={14} className="animate-spin text-text-muted" />}
              </div>
              {(canRefreshBedCharges || (canBill && visitId)) && (
                <div className="flex flex-wrap items-center gap-2">
                  {canRefreshBedCharges && (
                    <button
                      type="button"
                      onClick={handleRefreshBedCharges}
                      disabled={refreshBedCharges.isPending}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-border text-xs font-semibold text-text hover:bg-background transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      title="Re-check the nightly bed charge and post any outstanding nights now"
                    >
                      <RefreshCw
                        size={14}
                        className={refreshBedCharges.isPending ? 'animate-spin' : ''}
                      />
                      Refresh
                    </button>
                  )}
                  {canBill && visitId && status !== 'CANCELLED' && (
                    <button
                      type="button"
                      onClick={() => setShowInvoiceModal(true)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary-dark transition-colors"
                      title="Raise an invoice for the unbilled charges and collect payment"
                    >
                      <ReceiptText size={14} />
                      Quick Invoice
                    </button>
                  )}
                  {canBill && visitId && (
                    <Link
                      to={`/billing/invoices/new?patientId=${patientId}&visitId=${visitId}`}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-border text-xs font-semibold text-text hover:bg-background transition-colors"
                      title="Create an invoice from this visit's unbilled charges"
                    >
                      <ExternalLink size={14} />
                      Create Invoice
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ChargeTotalTile
                icon={<Clock size={15} />}
                label="Unbilled"
                value={totals.unbilled}
                tone="warning"
              />
              <ChargeTotalTile
                icon={<ReceiptText size={15} />}
                label="Total"
                value={invoicedTotal}
                tone="primary"
              />
              <ChargeTotalTile
                icon={<CheckCircle2 size={15} />}
                label="Paid"
                value={paidTotal}
                tone="success"
              />
              <ChargeTotalTile
                icon={<Banknote size={15} />}
                label="Due"
                value={dueTotal}
                tone={dueTotal > 0 ? 'danger' : 'success'}
              />
            </div>

            {paymentState === 'RISK_FUND' && (
              <div className="flex items-start gap-2.5 rounded-xl border border-warning/40 bg-warning/10 px-3.5 py-3">
                <ShieldAlert size={16} className="mt-0.5 shrink-0 text-warning" />
                <div className="min-w-0 space-y-0.5">
                  <p className="text-[0.6875rem] font-bold uppercase tracking-wide text-warning">
                    Risk Fund
                  </p>
                  <p className="text-[0.6875rem] leading-relaxed text-text-muted">
                    The outstanding balance
                    {writtenOffTotal > 0 ? ` (${money(writtenOffTotal)})` : ''} was written
                    off and moved to the Risk Fund. Nothing is left to collect from the
                    patient for this admission.
                  </p>
                </div>
              </div>
            )}

            {chargesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-primary" />
              </div>
            ) : charges.length === 0 ? (
              <p className="text-sm text-text-muted py-2">
                No charges yet. Bed charges post nightly; add services from the cart to bill them.
              </p>
            ) : (
              <div className="border border-border rounded-xl overflow-hidden">
                {canDeleteCharges && selectableCharges.length > 0 && (
                  <div className="flex items-center justify-between gap-2 border-b border-border bg-background/60 px-3 py-2">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedChargeIds(
                          allChargesSelected
                            ? []
                            : selectableCharges.map((charge) => charge.id),
                        )
                      }
                      className="flex items-center gap-2 text-[0.6875rem] font-semibold text-text-muted transition-colors hover:text-text"
                      aria-pressed={allChargesSelected}
                    >
                      <SelectionBox checked={allChargesSelected} />
                      Select unbilled ({selectableCharges.length})
                    </button>
                    {selectedCharges.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setConfirmBulkCharges(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-danger px-2.5 py-1 text-[0.6875rem] font-semibold text-white transition-colors hover:bg-danger/90"
                      >
                        <Trash2 size={12} />
                        Delete {selectedCharges.length}
                      </button>
                    )}
                  </div>
                )}
                <div className="max-h-80 overflow-y-auto divide-y divide-border">
                  {charges.map((charge) => {
                    const selectable = canDeleteCharges && !charge.isInvoiced;
                    const selected = selectedChargeIds.includes(charge.id);
                    return (
                      <div
                        key={charge.id}
                        className={`flex items-center gap-3 px-4 py-2.5 ${
                          selected ? 'bg-danger/5' : ''
                        }`}
                      >
                        {canDeleteCharges && (
                          <span className="shrink-0">
                            {selectable ? (
                              <button
                                type="button"
                                onClick={() => toggleChargeSelection(charge.id)}
                                aria-pressed={selected}
                                aria-label={`Select ${charge.chargeDescription}`}
                                className="block"
                              >
                                <SelectionBox checked={selected} />
                              </button>
                            ) : (
                              <span className="block h-4 w-4" aria-hidden="true" />
                            )}
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm text-text truncate"
                            title={charge.chargeDescription}
                          >
                            {charge.chargeDescription}
                          </p>
                          <p className="text-[0.6875rem] text-text-muted">
                            {formatOrdinalDateTimeLong(charge.createdAt)}
                          </p>
                        </div>
                        {/* Fixed-width cells so the quantity, amount and status
                            line up in one straight column down the list. */}
                        <div className="flex shrink-0 items-center gap-3">
                          {canEditChargeQuantity && (
                            <div className="flex w-[118px] items-center justify-end gap-1.5">
                              {!charge.isInvoiced &&
                                // Bed nights are posted automatically from the
                                // bed rate and the stay length — their quantity
                                // is not editable (enum: `bed_allocation`), and
                                // without the catalog service the API cannot
                                // re-price the line.
                                charge.type !== 'bed_allocation' &&
                                charge.service && (
                                  <>
                                    <span className="text-[0.625rem] font-semibold uppercase tracking-wide text-text-muted">
                                      Qty
                                    </span>
                                    <QuantityStepper
                                      value={
                                        quantityDrafts[charge.id] ??
                                        Number(charge.quantity || 1)
                                      }
                                      onChange={(next) =>
                                        handleChargeQuantityChange(charge, next)
                                      }
                                      min={chargeQuantityBounds(charge).min}
                                      max={chargeQuantityBounds(charge).max}
                                      step={chargeQuantityBounds(charge).step}
                                      size="sm"
                                      disabled={savingChargeId === charge.id}
                                      ariaLabel={`${charge.chargeDescription} quantity`}
                                    />
                                    {savingChargeId === charge.id && (
                                      <Loader2
                                        size={12}
                                        className="animate-spin text-primary"
                                      />
                                    )}
                                  </>
                                )}
                            </div>
                          )}
                          <span className="w-24 text-right text-sm font-medium tabular-nums text-text">
                            {money(Number(charge.totalAmount || 0))}
                          </span>
                          <span
                            className={`inline-flex w-[76px] justify-center rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide ${
                              charge.isInvoiced
                                ? 'bg-success/10 text-success'
                                : 'bg-warning/10 text-warning'
                            }`}
                          >
                            {charge.isInvoiced ? 'Invoiced' : 'Unbilled'}
                          </span>
                          {canDeleteCharges &&
                            (selectable ? (
                              <button
                                type="button"
                                onClick={() => setChargeToDelete(charge)}
                                className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                                title="Remove this unbilled charge from the bill"
                                aria-label={`Delete ${charge.chargeDescription}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            ) : (
                              <span className="w-[26px]" aria-hidden="true" />
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Invoices raised on this visit */}
          <div className="bg-surface border border-border rounded-2xl p-5 space-y-4 shadow-sm">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <ReceiptText size={18} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                  Invoices
                </h2>
                {billingStatusLoading && (
                  <Loader2 size={14} className="animate-spin text-text-muted" />
                )}
                {visitInvoices.length > 0 && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide text-primary">
                    {visitInvoices.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canDeleteInvoices && selectedInvoices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setConfirmBulkInvoices(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-danger px-2.5 py-1.5 text-[0.6875rem] font-semibold text-white transition-colors hover:bg-danger/90"
                  >
                    <Trash2 size={12} />
                    Delete {selectedInvoices.length}
                  </button>
                )}
                {canDeleteInvoices && deletableInvoices.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedInvoiceIds(
                        allInvoicesSelected
                          ? []
                          : deletableInvoices.map((invoice) => invoice.id),
                      )
                    }
                    aria-pressed={allInvoicesSelected}
                    className="inline-flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-[0.6875rem] font-semibold text-text-muted transition-colors hover:text-text"
                  >
                    <SelectionBox checked={allInvoicesSelected} />
                    Select unpaid ({deletableInvoices.length})
                  </button>
                )}
                {canBill && (
                  <Link
                    to="/billing/invoices"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[0.6875rem] font-semibold text-text hover:bg-background transition-colors"
                  >
                    <ExternalLink size={12} />
                    Open Billing
                  </Link>
                )}
              </div>
            </div>

            {billingStatusLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={18} className="animate-spin text-primary" />
              </div>
            ) : visitInvoices.length === 0 ? (
              <p className="py-2 text-sm text-text-muted">
                No invoice raised for this visit yet. Use Quick Invoice to bill the unbilled
                charges.
              </p>
            ) : (
              <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                {visitInvoices.map((invoice) => {
                  const paid = Number(invoice.paidAmount || 0);
                  const due = Number(invoice.dueAmount || 0);
                  // A written-off invoice has no due left but was not paid —
                  // label it explicitly instead of "Fully paid".
                  const isRiskFundInvoice = isRiskFundInvoiceStatus(invoice.status);
                  const writtenOff = Math.max(
                    0,
                    (Number(invoice.total) || 0) - paid,
                  );
                  const deletable =
                    canDeleteInvoices &&
                    paid <= 0 &&
                    !isRiskFundInvoiceStatus(invoice.status);
                  const selected = selectedInvoiceIds.includes(invoice.id);
                  return (
                    <div
                      key={invoice.id}
                      className={`flex items-center gap-3 px-4 py-2.5 ${
                        selected ? 'bg-danger/5' : ''
                      }`}
                    >
                      {canDeleteInvoices && (
                        <span className="shrink-0">
                          {deletable ? (
                            <button
                              type="button"
                              onClick={() => toggleInvoiceSelection(invoice.id)}
                              aria-pressed={selected}
                              aria-label={`Select invoice ${invoice.invoiceNumber}`}
                              className="block"
                            >
                              <SelectionBox checked={selected} />
                            </button>
                          ) : (
                            <span className="block h-4 w-4" aria-hidden="true" />
                          )}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-text">
                          {invoice.invoiceNumber || invoice.id.slice(0, 8)}
                        </p>
                        <p className="text-[0.6875rem] text-text-muted">
                          {formatOrdinalDateTimeLong(invoice.createdAt)}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {/* Fixed-width amount + status columns keep every row's
                            totals and dues on one straight vertical line. */}
                        <div className="w-24 shrink-0 text-right">
                          <p className="whitespace-nowrap text-sm font-medium tabular-nums text-text">
                            {money(Number(invoice.total || 0))}
                          </p>
                          <p
                            className={`whitespace-nowrap text-[0.6875rem] font-semibold tabular-nums ${
                              isRiskFundInvoice
                                ? 'text-warning'
                                : due > 0
                                  ? 'text-danger'
                                  : 'text-success'
                            }`}
                          >
                            {isRiskFundInvoice
                              ? `${money(writtenOff)} written off`
                              : due > 0
                                ? `${money(due)} due`
                                : 'Fully paid'}
                          </p>
                        </div>
                        <span
                          className={`inline-flex w-[84px] shrink-0 justify-center rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide ${
                            isRiskFundInvoice
                              ? 'bg-warning text-white ring-1 ring-warning/40'
                              : paid <= 0
                                ? 'bg-warning/10 text-warning'
                                : due > 0
                                  ? 'bg-primary/10 text-primary'
                                  : 'bg-success/10 text-success'
                          }`}
                        >
                          {isRiskFundInvoice
                            ? 'Risk fund'
                            : paid <= 0
                              ? 'Unpaid'
                              : due > 0
                                ? 'Partly paid'
                                : 'Paid'}
                        </span>
                        {canDeleteInvoices && (
                          <span className="flex w-[26px] shrink-0 justify-center">
                            {deletable && (
                              <button
                                type="button"
                                onClick={() => setInvoiceToDelete(invoice)}
                                className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                                title="Delete this unpaid invoice — its charges go back to unbilled"
                                aria-label={`Delete invoice ${invoice.invoiceNumber}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-[0.6875rem] text-text-muted">
              Only invoices with no payment recorded can be deleted. Deleting one releases its
              charges back to <span className="font-semibold text-text">Unbilled</span>, so the
              bill can be raised again correctly.
            </p>
          </div>

      </div>
      </div>

      <PatientDetailsModal
        open={showPatientModal}
        patient={patient}
        onClose={() => setShowPatientModal(false)}
        profileHref={patientId ? `/patients/${patientId}` : undefined}
      />

      <AdmissionReferralModal
        open={showReferralModal}
        admission={admission}
        onClose={() => setShowReferralModal(false)}
        onRequestRemove={() => {
          setShowReferralModal(false);
          setConfirmRemoveReferral(true);
        }}
      />

      <ReferralRemoveDialog
        open={confirmRemoveReferral}
        referralName={admission.referral?.name}
        commissionLabel={money(Number(admission.referralFlatAmount) || 0)}
        loading={updateAdmission.isPending}
        onConfirm={handleRemoveReferral}
        onCancel={() => setConfirmRemoveReferral(false)}
      />

      <AdmissionInvoiceModal
        variant="existing"
        open={showInvoiceModal}
        admission={admission}
        patientName={fullName}
        onDone={() => setShowInvoiceModal(false)}
        onViewInvoices={() => {
          setShowInvoiceModal(false);
          navigate('/billing/invoices');
        }}
        onDischarge={handleDischarge}
        discharging={dischargeAdmission.isPending}
        onGoToList={() => {
          setShowInvoiceModal(false);
          navigate(backTo);
        }}
      />

      {showTransferBed && canManageBed && (
        <AdmissionTransferBedModal
          open={showTransferBed}
          admissionId={admission.id}
          currentBedLabel={bedLabel}
          onClose={() => setShowTransferBed(false)}
        />
      )}

      {showCareTeam && (
        <AdmissionCareTeamModal
          open={showCareTeam}
          admissionId={admission.id}
          onClose={() => setShowCareTeam(false)}
        />
      )}

      {canManageAdmissionMeta && (
        <AdmissionDetailsModal
          open={showAdmissionDetails}
          admissionId={admission.id}
          doctorId={admission.doctorId}
          doctorName={admission.doctor?.name}
          currentType={admission.admissionType}
          admittedAt={admission.admittedAt || admission.admissionDate}
          isEmergency={isEmergency}
          referredById={admission.referredById}
          referredByName={admission.referredBy?.name}
          onClose={() => setShowAdmissionDetails(false)}
        />
      )}

      <ConfirmDialog
        open={!!dueConfirm}
        variant="primary"
        title="Discharge with an unpaid balance?"
        confirmLabel="Discharge with due"
        loading={dischargeAdmission.isPending}
        description={
          <>
            {fullName} will leave with{' '}
            <strong className="text-text">{money(dueConfirm?.amount ?? 0)}</strong>{' '}
            unpaid. The invoice stays open in Billing → Invoices and can be collected
            later.
          </>
        }
        onConfirm={() => runDischarge(true)}
        onCancel={() => setDueConfirm(null)}
      />

      <ConfirmDialog
        open={!!chargeToDelete}
        title="Remove this charge?"
        confirmLabel="Remove charge"
        loading={deleteCharge.isPending}
        description={
          <>
            <strong className="text-text">{chargeToDelete?.chargeDescription}</strong> (
            {money(Number(chargeToDelete?.totalAmount || 0))}) will be removed from the bill
            and the patient's ledger is corrected. This cannot be undone.
          </>
        }
        onConfirm={handleDeleteCharge}
        onCancel={() => setChargeToDelete(null)}
      />

      <ConfirmDialog
        open={confirmBulkCharges}
        title={`Remove ${selectedCharges.length} charge${selectedCharges.length === 1 ? '' : 's'}?`}
        confirmLabel={`Remove ${selectedCharges.length}`}
        loading={bulkDeleteCharges.isPending}
        description={
          <>
            The selected unbilled charges worth{' '}
            <strong className="text-text">
              {money(
                selectedCharges.reduce(
                  (sum, charge) => sum + Number(charge.totalAmount || 0),
                  0,
                ),
              )}
            </strong>{' '}
            will be removed from this visit and the patient's ledger is corrected.
          </>
        }
        onConfirm={handleBulkDeleteCharges}
        onCancel={() => setConfirmBulkCharges(false)}
      />

      <ConfirmDialog
        open={!!invoiceToDelete}
        title="Delete this invoice?"
        confirmLabel="Delete invoice"
        loading={deleteInvoice.isPending}
        description={
          <>
            Invoice{' '}
            <strong className="text-text">
              {invoiceToDelete?.invoiceNumber || invoiceToDelete?.id.slice(0, 8)}
            </strong>{' '}
            ({money(Number(invoiceToDelete?.total || 0))}) will be removed and its charges go
            back to <span className="font-semibold text-text">Unbilled</span>. Only invoices
            without a recorded payment can be deleted.
          </>
        }
        onConfirm={handleDeleteInvoice}
        onCancel={() => setInvoiceToDelete(null)}
      />

      <ConfirmDialog
        open={confirmBulkInvoices}
        title={`Delete ${selectedInvoices.length} invoice${selectedInvoices.length === 1 ? '' : 's'}?`}
        confirmLabel={`Delete ${selectedInvoices.length}`}
        loading={bulkDeleteInvoices.isPending}
        description={
          <>
            Every charge on {selectedInvoices.length === 1 ? 'this invoice' : 'these invoices'}{' '}
            is released back to <span className="font-semibold text-text">Unbilled</span>, and
            the visit's total recalculates. Invoices with a recorded payment are kept.
          </>
        }
        onConfirm={handleBulkDeleteInvoices}
        onCancel={() => setConfirmBulkInvoices(false)}
      />
    </div>
  );
}

/** Shared small checkbox used by the charge and invoice bulk selection rows. */
function SelectionBox({ checked }: { checked: boolean }) {
  return (
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[5px] border transition ${
        checked ? 'border-primary bg-primary text-white' : 'border-border bg-surface'
      }`}
    >
      {checked && <CheckCircle2 size={11} />}
    </span>
  );
}

/** Small pencil action shown in an InfoTile's label row. */
function EditTileButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="ml-auto shrink-0 rounded-md p-1 text-text-muted transition-colors hover:bg-primary/10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <Pencil size={12} />
    </button>
  );
}

function InfoTile({
  icon,
  label,
  value,
  capitalize,
  className = '',
  action,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  capitalize?: boolean;
  className?: string;
  /** Optional trailing control (e.g. an edit pencil) in the label row. */
  action?: ReactNode;
}) {
  return (
    <div
      className={`min-w-0 rounded-xl border border-border/60 bg-background/40 px-2.5 py-2 ${className}`}
    >
      <div className="flex items-center gap-1.5 text-text-muted">
        <span className="shrink-0 text-primary [&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
        <p className="min-w-0 text-[0.625rem] font-semibold uppercase leading-tight tracking-wide">
          {label}
        </p>
        {action}
      </div>
      {typeof value === 'string' ? (
        <p
          className={`mt-1 break-words text-[0.8125rem] font-semibold leading-snug text-text ${
            capitalize ? 'capitalize' : ''
          }`}
        >
          {value}
        </p>
      ) : (
        <div className="mt-1 text-[0.8125rem] leading-snug">{value}</div>
      )}
    </div>
  );
}

function ChargeTotalTile({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone: 'warning' | 'success' | 'primary' | 'danger';
}) {
  const toneClass =
    tone === 'warning'
      ? 'bg-warning/10 text-warning'
      : tone === 'success'
        ? 'bg-success/10 text-success'
        : tone === 'danger'
          ? 'bg-danger/10 text-danger'
          : 'bg-primary/10 text-primary';
  return (
    <div className="rounded-xl border border-border bg-background p-3">
      <div
        className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md text-[0.625rem] font-bold uppercase tracking-wide ${toneClass}`}
      >
        {icon}
        {label}
      </div>
      <p className="text-base font-semibold text-text mt-1.5">{money(Number(value || 0))}</p>
    </div>
  );
}

interface ServiceCartLine {
  service: any;
  quantity: number;
  /**
   * Overwritten price per unit (৳). Only used for Emergency service lines on an
   * Emergency admission; every other line bills the catalog price.
   */
  unitPrice?: number;
}

/** Prices one cart line with the same min/rounding rules the API applies. */
function cartLinePricing(line: ServiceCartLine) {
  const service =
    line.unitPrice != null ? { ...line.service, price: line.unitPrice } : line.service;
  return resolveServicePricing(service, line.quantity);
}

/** The catalog rate for one unit, ignoring any cart overwrite. */
function cartLineCatalogUnitPrice(line: ServiceCartLine) {
  return resolveServicePricing(line.service, line.quantity).unitPrice;
}

/**
 * Add-to-cart style service picker. The catalog is fetched on demand and each
 * selected service is priced from the catalog, including tax, before it is
 * posted to the admission's visit as unbilled charge events. On an emergency
 * stay, Emergency service lines may be billed at an overwritten price.
 */
function ServiceCartPanel({
  admissionId,
  isEmergency = false,
}: {
  admissionId: string;
  /** Emergency stay: its Emergency service lines may overwrite their price. */
  isEmergency?: boolean;
}) {
  const { data: servicesData, isLoading } = useServices(1, 1000);
  const { data: serviceTypesData } = useActiveServiceTypes();
  const addServices = useAddAdmissionServices();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [cart, setCart] = useState<Map<string, ServiceCartLine>>(() => new Map());
  const [error, setError] = useState<string | null>(null);

  const services = useMemo(
    () => (servicesData?.data || []).filter((s: any) => s.isActive !== false),
    [servicesData],
  );

  // Active service types (from the API) drive the "by type" filter and let the
  // search match the human-readable type name in addition to the stored code.
  const serviceTypes = serviceTypesData?.data || [];
  const serviceTypeName = (code?: string) =>
    serviceTypes.find((type) => type.code === code)?.name || code || '';

  // Codes of the managed "Emergency" Service Type — the only lines whose price
  // may be overwritten, and only on an emergency stay.
  const emergencyServiceTypeCodes = useMemo(() => {
    const codes = new Set<string>();
    (serviceTypesData?.data || []).forEach((type: any) => {
      if (isEmergencyServiceType(type?.code, type?.name)) {
        codes.add(normalizeServiceTypeCode(type?.code));
      }
    });
    return codes;
  }, [serviceTypesData]);

  const canOverrideServicePrice = (service: any) =>
    isEmergency && isEmergencyService(service?.serviceType, emergencyServiceTypeCodes);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services.filter((s: any) => {
      if (typeFilter !== 'all' && s.serviceType !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        s.name,
        s.code,
        s.serviceType,
        s.serviceType ? serviceTypeName(s.serviceType) : '',
        s.description,
        s.departmentCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, search, typeFilter, serviceTypes]);

  const lines = useMemo(() => Array.from(cart.values()), [cart]);

  // Count distinct services in the cart — a duration (e.g. 2.5 hours) is not an
  // "item", so summing quantities would be misleading.
  const itemCount = lines.length;

  const subtotal = useMemo(
    () => round2(lines.reduce((sum, line) => sum + cartLinePricing(line).amount, 0)),
    [lines],
  );
  const tax = useMemo(
    () =>
      round2(
        lines.reduce((sum, line) => {
          const amount = cartLinePricing(line).amount;
          return sum + (amount * Number(line.service.taxPercent || 0)) / 100;
        }, 0),
      ),
    [lines],
  );
  const total = round2(subtotal + tax);

  // Clicking an already-added service nudges its quantity up one step, so the
  // catalog doubles as a quick "add another" action.
  const addService = (service: any) =>
    setCart((prev) => {
      const next = new Map(prev);
      const existing = prev.get(service.id);
      const bounds = serviceQuantityBounds(service);
      const requested = existing ? existing.quantity + bounds.step : 1;
      const quantity = resolveServicePricing(
        service,
        Math.min(bounds.max, requested),
      ).quantity;
      // Keep any price overwrite applied to the existing line.
      next.set(service.id, { service, quantity, unitPrice: existing?.unitPrice });
      return next;
    });

  // Quantity edits are committed through the pricing rules, so the cart always
  // shows the units that will actually be billed.
  const setServiceQuantity = (id: string, rawQuantity: number) =>
    setCart((prev) => {
      const existing = prev.get(id);
      if (!existing) return prev;
      const bounds = serviceQuantityBounds(existing.service);
      const quantity = Math.min(
        bounds.max,
        Math.max(bounds.min, resolveServicePricing(existing.service, rawQuantity).quantity),
      );
      if (quantity === existing.quantity) return prev;
      const next = new Map(prev);
      next.set(id, { ...existing, quantity });
      return next;
    });

  /**
   * Overwrites one cart line's unit price (emergency lines only). Setting it
   * back to the catalog rate drops the overwrite so the line bills normally.
   */
  const setServicePrice = (id: string, rawPrice: number) =>
    setCart((prev) => {
      const existing = prev.get(id);
      if (!existing) return prev;
      const price = round2(Math.max(0, Number(rawPrice) || 0));
      const catalogPrice = round2(Number(existing.service?.price) || 0);
      const next = new Map(prev);
      next.set(id, {
        ...existing,
        unitPrice: price === catalogPrice ? undefined : price,
      });
      return next;
    });

  /** Drops the overwrite so the line bills the catalog price again. */
  const resetServicePrice = (id: string) =>
    setCart((prev) => {
      const existing = prev.get(id);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(id, { ...existing, unitPrice: undefined });
      return next;
    });

  // Up/Down moves through the service catalog, Enter adds the highlighted one.
  const serviceNav = useListKeyboardNav({
    itemCount: filtered.length,
    onSelect: (index) => {
      const service = filtered[index];
      if (service) addService(service);
    },
    resetKey: search,
  });

  const removeService = (id: string) =>
    setCart((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

  const clearCart = () => setCart(new Map());

  const handleAdd = async () => {
    setError(null);
    const billable = lines.filter((line) => Number(line.quantity) > 0);
    if (billable.length === 0) {
      setError('Select at least one service to add');
      return;
    }
    try {
      await addServices.mutateAsync({
        admissionId,
        items: billable.map((line) => ({
          serviceId: line.service.id,
          quantity: line.quantity,
          // Emergency lines on an emergency stay may carry an overwritten unit
          // price; the server rejects an override anywhere else.
          ...(line.unitPrice != null && canOverrideServicePrice(line.service)
            ? { unitPrice: line.unitPrice }
            : {}),
        })),
      });
      clearCart();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to add services',
      );
    }
  };

  return (
    <aside className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm xl:h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/5 to-transparent px-4 py-3.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="relative shrink-0 rounded-xl bg-primary/10 p-2">
            <ShoppingCart size={18} className="text-primary" />
            {itemCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[0.625rem] font-bold text-white">
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold leading-tight text-text">Add Services</h2>
            <p className="truncate text-[0.6875rem] text-text-muted">
              Charge to this admission's bill
            </p>
          </div>
        </div>
        {itemCount > 0 && (
          <button
            type="button"
            onClick={clearCart}
            className="shrink-0 text-[0.6875rem] font-medium text-text-muted transition-colors hover:text-danger"
          >
            Clear
          </button>
        )}
      </div>

      {/* Search & type filter */}
      <div className="space-y-2 border-b border-border p-3">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={serviceNav.onKeyDown}
              placeholder="Search name, code or type..."
              className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-8 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted transition-colors hover:text-text"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-[124px] shrink-0 rounded-lg border border-border bg-background px-2.5 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Types</option>
            {serviceTypes.map((type) => (
              <option key={type.id} value={type.code}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        <p className="px-0.5 text-[0.625rem] text-text-muted">
          Use ↑ ↓ to browse · Enter to add
        </p>
      </div>

      {/* Selected lines */}
      {lines.length > 0 && (
        <div className="border-b border-border bg-success/5">
          <div className="flex items-center justify-between px-4 py-2">
            <span className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-success">
              <ShoppingCart size={12} />
              In cart · {itemCount} {itemCount === 1 ? 'service' : 'services'}
            </span>
          </div>
          <div className="max-h-56 space-y-1.5 overflow-y-auto px-3 pb-3">
            {lines.map((line) => {
              const pricing = cartLinePricing(line);
              const catalogUnitPrice = cartLineCatalogUnitPrice(line);
              const priceOverridden = line.unitPrice != null;
              const bounds = serviceQuantityBounds(line.service);
              const canOverride = canOverrideServicePrice(line.service);
              return (
                <div
                  key={line.service.id}
                  className="rounded-lg border border-success/30 bg-success/10 px-2.5 py-1.5"
                >
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-success" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-text">
                        {line.service.name}
                        {priceOverridden && (
                          <span className="ml-1.5 rounded bg-warning/15 px-1 py-px align-middle text-[0.5625rem] font-bold uppercase tracking-wide text-warning">
                            Overwritten
                          </span>
                        )}
                      </p>
                      <p className="truncate text-[0.625rem] font-medium text-success">
                        {money(pricing.unitPrice)}
                        {pricing.unitLabel ? ` / ${pricing.unitLabel}` : ''}
                        <span className="text-text-muted"> · </span>
                        <span className="text-text">{money(pricing.amount)}</span>
                        {priceOverridden && catalogUnitPrice !== pricing.unitPrice && (
                          <span className="text-text-muted">
                            {' '}
                            · catalog {money(catalogUnitPrice)}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeService(line.service.id)}
                      className="shrink-0 rounded-lg p-1 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                      title="Remove"
                      aria-label={`Remove ${line.service.name}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 pl-6">
                    <span className="text-[0.625rem] font-medium uppercase tracking-wide text-text-muted">
                      Qty
                    </span>
                    <QuantityStepper
                      value={line.quantity}
                      onChange={(next) => setServiceQuantity(line.service.id, next)}
                      min={bounds.min}
                      max={bounds.max}
                      step={bounds.step}
                      size="sm"
                      ariaLabel={`${line.service.name} quantity`}
                    />
                  </div>
                  {/* Emergency stays may overwrite the price of Emergency
                      services; every other line bills the catalog price. */}
                  {canOverride && (
                    <div className="mt-1 flex items-center justify-between gap-2 border-t border-dashed border-success/30 pl-6 pt-1">
                      <span className="flex items-center gap-1 text-[0.625rem] font-medium uppercase tracking-wide text-text-muted">
                        Unit price
                        <InfoHint
                          label="Unit price"
                          en="Emergency admission: this Emergency service's price can be overwritten for this stay. Leave it at the catalog price to bill normally."
                          bn="ইমার্জেন্সি ভর্তি: এই ইমার্জেন্সি সার্ভিসের মূল্য এই ভর্তির জন্য পরিবর্তন করা যাবে। স্বাভাবিক বিলের জন্য ক্যাটালগ মূল্যই রাখুন।"
                        />
                      </span>
                      <div className="flex items-center gap-1.5">
                        {priceOverridden && (
                          <button
                            type="button"
                            onClick={() => resetServicePrice(line.service.id)}
                            className="rounded px-1 text-[0.625rem] font-semibold text-primary transition-colors hover:text-primary-dark cursor-pointer"
                            title={`Back to catalog price ${money(catalogUnitPrice)}`}
                          >
                            Reset
                          </button>
                        )}
                        <div className="relative">
                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[0.6875rem] text-text-muted">
                            ৳
                          </span>
                          <ValidatedNumberInput
                            value={pricing.unitPrice}
                            onChange={(next) => setServicePrice(line.service.id, next)}
                            max={9999999999.99}
                            className={`w-24 rounded-lg border bg-surface py-1 pl-5 pr-1.5 text-right text-xs font-semibold tabular-nums text-text focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                              priceOverridden ? 'border-warning/50' : 'border-border'
                            }`}
                            title="Overwrite the unit price for this emergency service"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Catalog */}
      <div
        ref={serviceNav.listRef}
        tabIndex={0}
        onKeyDown={serviceNav.onKeyDown}
        className="min-h-0 max-h-[360px] overflow-y-auto outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary/40 xl:max-h-none xl:flex-1"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={18} className="animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <Package className="mx-auto mb-2 h-7 w-7 text-text-muted" />
            <p className="text-sm text-text-muted">No services found</p>
            <p className="mt-1 text-xs text-text-muted">Try a different search</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((svc: any, index: number) => {
              const cartLine = cart.get(svc.id);
              const inCart = Boolean(cartLine);
              const price = Number(svc.price || 0);
              const taxPercent = Number(svc.taxPercent || 0);
              const highlighted = index === serviceNav.highlightedIndex && !inCart;
              return (
                <div
                  key={svc.id}
                  data-nav-index={index}
                  onMouseEnter={() => serviceNav.setHighlightedIndex(index)}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                    inCart
                      ? 'bg-success/10 ring-1 ring-inset ring-success/30'
                      : highlighted
                        ? 'bg-background ring-1 ring-inset ring-primary/40'
                        : 'hover:bg-background'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => addService(svc)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    title={inCart ? 'Add one more' : 'Add to cart'}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
                        inCart
                          ? 'bg-success/20 text-success'
                          : 'bg-primary/10 text-primary'
                      }`}
                    >
                      {inCart ? (
                        <CheckCircle2 size={15} />
                      ) : (
                        <Plus size={15} />
                      )}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={`block truncate text-sm font-medium ${
                          inCart ? 'text-success' : 'text-text'
                        }`}
                      >
                        {svc.name}
                      </span>
                      <span className="block truncate text-[0.6875rem] text-text-muted">
                        {[svc.code, serviceTypeName(svc.serviceType), taxPercent > 0 ? `${taxPercent}% tax` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    {cartLine && (
                      <span className="rounded-md bg-success/15 px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide text-success">
                        Added
                        {cartLine.quantity !== 1
                          ? ` ×${formatQuantity(cartLine.quantity)}`
                          : ''}
                      </span>
                    )}
                    <span className="text-sm font-semibold text-primary">
                      {money(price)}
                      {isQuantityBilled(svc.billingUnit) && (
                        <span className="text-[0.625rem] font-normal text-text-muted">
                          {' '}/ {svc.unitLabel?.trim() || defaultUnitLabel(svc.billingUnit)}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer / checkout */}
      <div className="space-y-3 border-t border-border bg-surface p-4">
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-text-muted">Subtotal</span>
            <span className="font-medium text-text">{money(subtotal)}</span>
          </div>
          {tax > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Tax</span>
              <span className="font-medium text-text">{money(tax)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-border pt-1.5">
            <span className="text-sm font-semibold text-text">Total</span>
            <span className="text-base font-bold text-primary">{money(total)}</span>
          </div>
        </div>

        {error && (
          <p className="flex items-center gap-1.5 text-xs text-danger">
            <AlertCircle size={12} />
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleAdd}
          disabled={addServices.isPending || lines.length === 0}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {addServices.isPending ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <ShoppingCart size={15} />
          )}
          {lines.length > 0 ? `Add ${itemCount} to Bill · ${money(total)}` : 'Add to Bill'}
        </button>
        <p className="text-center text-[0.625rem] text-text-muted">
          Services post as unbilled charges and flow into the next invoice.
        </p>
      </div>
    </aside>
  );
}
